import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MenuItemService } from '@core/services/menu-item.service';
import { take } from 'rxjs';
import { IMenuItem } from '@core/interfaces/imenu-item';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { ToastrService } from 'ngx-toastr';
import { IModalData } from '@core/interfaces/imodal-data';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmCancelComponent } from 'src/app/component/shared/confirm-cancel/confirm-cancel.component';
import { Router } from '@angular/router';
import { ModalFormComponent } from 'src/app/component/shared/modal-form/modal-form.component';
import { Validators } from '@angular/forms';
import { faPlus, faPencilAlt, faEye, faEyeSlash, faBars, faTrash } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'app-menu-editor',
    templateUrl: './menu-editor.component.html',
    styleUrls: ['./menu-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MenuEditorComponent {
  constructor(
    private menuItem: MenuItemService,
    private toastr: ToastrService,
    private modal: NgbModal,
    private router: Router
  ) {}

  editablePages!: IMenuItem[];
  orderChanged: boolean = false;

  faPlus = faPlus;
  faPencilAlt = faPencilAlt;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  faBars = faBars;
  faTrash = faTrash;

  ngOnInit() {
    this.getMenuItems();
  }

  onSubmit(): void {
    const payload = { menuOrder: this.editablePages };
    this.menuItem
      .updateOrder(payload)
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.toastr.success('Menu Order Updated!');
          this.orderChanged = false;
        } else {
          this.toastr.error('Menu Order was not updated!');
        }
      });
  }

  drop(
    event: CdkDragDrop<string[]> | any,
    index: number = 0,
    isParent: boolean = false
  ) {
    if (isParent) {
      moveItemInArray(
        this.editablePages,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      if (event.previousContainer === event.container) {
        moveItemInArray(
          this.editablePages[index]['subMenuItems'] as IMenuItem[],
          event.previousIndex,
          event.currentIndex
        );
      } else {
        const prevIndex = Number(event.previousContainer.id);
        transferArrayItem(
          this.editablePages[prevIndex]['subMenuItems'] as IMenuItem[],
          this.editablePages[index]['subMenuItems'] as IMenuItem[],
          event.previousIndex,
          event.currentIndex
        );
      }
    }
    this.orderChanged = true;
  }

  togglePublished(primaryIndex: number, secondaryIndex?: number): void {
    if (secondaryIndex == null) {
      this.editablePages[primaryIndex].isPublished =
        !this.editablePages[primaryIndex].isPublished;
      if (this.editablePages[primaryIndex].subMenuItems!.length > 0) {
        this.editablePages[primaryIndex].subMenuItems?.forEach(
          (smi: IMenuItem) => {
            smi.isPublished = this.editablePages[primaryIndex].isPublished;
          }
        );
      }
    } else {
      if (
        !this.editablePages[primaryIndex].isPublished &&
        !this.editablePages[primaryIndex].subMenuItems![secondaryIndex!]
          .isPublished
      ) {
        this.toastr.error('Cannot publish unless trigger is published.');
        return;
      }
      this.editablePages[primaryIndex].subMenuItems![
        secondaryIndex
      ].isPublished =
        !this.editablePages[primaryIndex].subMenuItems![secondaryIndex]
          .isPublished;
    }
  }

  getMenuItems(): void {
    this.menuItem
      .getEditable()
      .pipe(take(1))
      .subscribe((res) => {
        this.editablePages = res;
      });
  }

  delete(index: IMenuItem): void {
    const modalData: IModalData = {
      title: 'Confirm Delete',
      body: `Are you sure you want to delete <b>${index?.name}</b>?`,
      cancelBtnText: 'Cancel',
      confirmBtnText: 'Confirm',
    };
    const modalRef = this.modal.open(ConfirmCancelComponent);
    modalRef.componentInstance.data = modalData;
    modalRef.result
      .then((_res: any) => {
        this.menuItem
          .delete(index.id, `"${index?.name}" successfully deleted!`)
          .pipe(take(1))
          .subscribe((res: boolean) => {
            this.getMenuItems();
          });
      })
      .catch((_err: any) => {});
  }

  confirmDiscard(): void {
    if (!this.orderChanged) {
      this.router.navigateByUrl('/admin');
      return;
    }
    const modalData: IModalData = {
      title: 'Confirm Discard Changes',
      body: 'Are you sure you want to discard your changes?',
      cancelBtnText: 'Continue Editing',
      confirmBtnText: 'Discard Unsaved Changes',
    };
    const modalRef = this.modal.open(ConfirmCancelComponent, {
      centered: true,
    });
    modalRef.componentInstance.data = modalData;
    modalRef.result
      .then((_res: any) => {
        this.router.navigateByUrl('/admin');
      })
      .catch((_err: any) => false);
  }

  createNewTrigger(): void {
    const modalRef = this.modal.open(ModalFormComponent, { centered: true });
    const modalData: IModalData = {
      formInputs: [
        {
          controlName: 'name',
          label: 'Trigger Name',
          type: 'text',
          validators: [Validators.required, Validators.maxLength(25)],
          maxLength: 25,
          helper: '(Max length: 25 characters)',
          required: true,
        },
      ],
      title: 'Add New Trigger',
      confirmBtnText: 'Add Trigger',
      cancelBtnText: 'Cancel',
    };
    modalRef.componentInstance.data = modalData;
    modalRef.result
      .then((res: any) => {
        res.sortOrder = 100;
        this.menuItem
          .add(res, 'Trigger Added')
          .pipe(take(1))
          .subscribe((res: any) => {
            this.getMenuItems();
          });
      })
      .catch((_err: any) => false);
  }

  updateTrigger(triggerName: string, index: number): void {
    const modalRef = this.modal.open(ModalFormComponent, { centered: true });
    const modalData: IModalData = {
      formInputs: [
        {
          controlName: 'name',
          label: 'Trigger Name',
          type: 'text',
          validators: [Validators.required, Validators.maxLength(25)],
          maxLength: 25,
          helper: '(Max length: 25 characters)',
          required: true,
        },
      ],
      title: 'Edit Trigger Name',
      body: `<p>Current Name: <b>${triggerName}</b></p>`,
      data: triggerName,
      confirmBtnText: 'Save',
      cancelBtnText: 'Cancel',
    };
    modalRef.componentInstance.data = modalData;
    modalRef.result
      .then((res: { name: string }) => {
        this.editablePages[index].name = res.name;
      })
      .catch((_err: any) => false);
  }

  updateExternal(externalItem: IMenuItem, index: number): void {
    const modalRef = this.modal.open(ModalFormComponent, { centered: true });
    const modalData: IModalData = {
      formInputs: [
        {
          controlName: 'name',
          label: 'Link Name',
          type: 'text',
          validators: [Validators.required, Validators.maxLength(25)],
          maxLength: 25,
          helper: '(Max length: 25 characters)',
          required: true,
        },
        {
          controlName: 'hrefLink',
          label: 'Link Url',
          type: 'url',
          validators: [Validators.required],
          required: true,
        },
      ],
      title: 'Update External Link',
      body: `<p>Current Name: <b>${externalItem.name}</b></p>`,
      data: {
        name: externalItem.name,
        hrefLink: externalItem.hrefLink,
      },
      confirmBtnText: 'Save',
      cancelBtnText: 'Cancel',
    };
    modalRef.componentInstance.data = modalData;
    modalRef.result
      .then((res: { name: string; hrefLink: string }) => {
        externalItem.name = res.name;
        externalItem.hrefLink = res.hrefLink;
      })
      .catch((_err: any) => false);
  }

  editPage(itemId: number, isEditable?: boolean): void {
    if (isEditable == false) {
      this.toastr.error('This page is currently unavailable to edit.');
      return;
    }
    this.router.navigateByUrl(`/admin/page/${itemId}`);
  }
}
